/** @jsxImportSource @emotion/react */
import { DISABLED, DROP_SHADOW, LargeText, MediumText, SmallText, XXXXLargeText } from "../styles/styles"
import { BLUE_CREEK, DARK_BLUE_CREEK, WHITE } from "../styles/colors"
import { Dropdown } from "../forms/elements/select/Dropdown"
import { useDispatch, useSelector } from "react-redux"
import { IAppState } from "../core/redux/IAppState"
import { setLocale } from "../language/resources/setLocaleAction"
import { resourceMap } from "../language/resources/resourceMap"
import { Dispatch, SetStateAction, useState } from "react"
import { updateUserPreferencesAction } from "../admin/userManagement/updateUserPreferenceAction"
import { getResource } from "../language/resources/getResource"
import { FADE_IN_KEYFRAMES } from "../styles/animations"
import { format } from "date-fns";
import { setDateFormat } from "../language/setDefaultDate"
import { getUserAction } from "../auth/getUserInfoAction"
import { css } from "@emotion/react"

const modalContainer = css `
    width: 400px; 
    height: 300px;
    background: #f3f3f3;
    ${DROP_SHADOW};
    border: ${BLUE_CREEK} solid 3px;
    padding: 15px;
    text-align: center;
`
const floatingModalTitle = css `
    ${XXXXLargeText}
    font-weight: bold;
`
const floatingModalInfo = css `
    ${SmallText}
    color: #a3a3a2;
    margin-top: 15px;
`

const floatingModalSelect = css `
    width: 45%;
    display: inline-block;
    margin: 5px;
    margin-top: 40px;

    select {
        border-left: rgb(118, 118, 118) solid 1px;
    }
`

const mappingButtonSubmit = css `
  text-align: center;
  text-decoration: none;
  ${MediumText};
  animation: 1s ${FADE_IN_KEYFRAMES};
  cursor: pointer;

  background-color: ${BLUE_CREEK}; 
  border: solid 1px ${WHITE};
  padding: 14px 30px;
  color: ${WHITE};
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 20px;
  margin-top: 55px;
  width: 200px;
  
  &:hover {
    background-color: ${DARK_BLUE_CREEK}; 
    color: ${WHITE};
  }

  &:disabled {
    ${DISABLED}
  }
`;

type Props = {
    resources: Record<string, any>,
    currentDateFormat: string,
    setIsOpen: Dispatch<SetStateAction<boolean>>
}

type PreferencesEntry = {
    value: string,
    label: string
}

export const PreferencesModal = (props: Props) => {
    const user = useSelector((state: IAppState) => state.userState);
    const resortLanguage = useSelector((state: IAppState) => state.resortToolsConfig.defaultLanguage === "" ? "en_us" : state.resortToolsConfig.defaultLanguage);
    const resortDateFormat = useSelector((state: IAppState) => state.resortToolsConfig.defaultDateFormat === "" ? "MM-dd-yyyy" : state.resortToolsConfig.defaultDateFormat);
    const [selectedLanguage, setSelectedLanguage] = useState<PreferencesEntry>(
        user.languagePreference && user.languagePreference !== "" ?
        {value: user.languagePreference, label: resourceMap[user.languagePreference]["LOCALE_NAME"]} :
        {value: "resortdefault", label: getResource(props.resources, "USER_PREFERENCE_MODAL_RESORT_DEFAULT") + " (" + resourceMap[resortLanguage]["LOCALE_NAME"] + ")"}
    );
    const [selectedDateFormat, setSelectedDateFormat] = useState<PreferencesEntry>(
        user.datePreference && user.datePreference !== "" ?
        {value: user.datePreference.replaceAll('-','/'), label: format(new Date(), user.datePreference.replaceAll('-','/'))}:
        {value: "resortdefault", label: (getResource(props.resources, "USER_PREFERENCE_MODAL_RESORT_DEFAULT") + " (" + resortDateFormat.replaceAll('-', '/') + ")")});

    const dispatch = useDispatch();
    const dateTimeArray : PreferencesEntry[] = [
        {value: "resortdefault", label: (getResource(props.resources, "USER_PREFERENCE_MODAL_RESORT_DEFAULT") + " (" + resortDateFormat.replaceAll('-', '/').toLowerCase() + ")")},
        {value: "MM/dd/yyyy", label: "mm/dd/yyyy"},
        {value: "dd/MM/yyyy", label: "dd/mm/yyyy"},
        {value: "yyyy/MM/dd", label: "yyyy/mm/dd"},
    ]

    const getMenuValue = (item: PreferencesEntry) => item.value;
    const getMenuLabel = (item: PreferencesEntry) => item.label;

    const generateMenuItems = (resources: Record<string, Record<string, any>>) => {
        var items: PreferencesEntry[] = [{value: "resortdefault", label: getResource(props.resources, "USER_PREFERENCE_MODAL_RESORT_DEFAULT") + " (" + resources[resortLanguage]["LOCALE_NAME"] + ")"}];
        Object.keys(resources).forEach((resourceKey) => {
            items.push({value: resourceKey, label: resources[resourceKey]["LOCALE_NAME"]});
        })
        return items;
    }

    const saveChanges = (resources: Record<string, Record<string, any>>) => {
        var languageKey = props.resources["LOCALE_KEY"];
        var dateKey = props.currentDateFormat;
        if (selectedLanguage.value !== props.resources["LOCALE_KEY"]) {
            languageKey = "resortdefault";
            Object.keys(resources).forEach((key) => {
                if (key === selectedLanguage.value) {
                    languageKey = key;
                }
            });
            if (languageKey !== "resortdefault") {
                dispatch(setLocale(languageKey));
            }
            else {
                dispatch(setLocale(resortLanguage));
            }
        }
        if (selectedDateFormat && selectedDateFormat.value !== props.currentDateFormat) {
            dateKey = selectedDateFormat.value;
            if (dateKey !== "resortdefault") {
                dispatch(setDateFormat(dateKey));
            }
            else {
                dispatch(setDateFormat(resortDateFormat.replaceAll('-', '/')));
            }
        }
        dispatch(updateUserPreferencesAction(user, languageKey, dateKey));
        window.setTimeout(() => dispatch(getUserAction(user.userId)), 1000);
        props.setIsOpen(false);
    }

    return (
    <div css={modalContainer}>
        <div css={floatingModalTitle}>
            <span>{getResource(props.resources, 'USER_PREFERENCE_MODAL_TITLE')}</span>
        </div>
        <div css={floatingModalInfo}> 
            <span>{getResource(props.resources, 'USER_PREFERENCE_MODAL_INFO')}</span>
        </div>
        <div css={floatingModalSelect}>
            <span css={css`display:inline-block; margin-bottom: 5px; ${LargeText}`}>{getResource(props.resources, 'USER_PREFERENCE_MODAL_LANGUAGE')}</span>
            <Dropdown<PreferencesEntry>
                resources={props.resources}
                items={generateMenuItems(resourceMap)}
                getValue={getMenuValue}
                getLabel={getMenuLabel}
                selectedItem={selectedLanguage}
                noItemsMessage={getResource(props.resources, 'USER_PREFERENCE_MODAL_NO_LANGUAGES_ERROR')}
                placeholderMessage={getResource(props.resources, 'USER_PREFERENCE_MODAL_LANGUAGE_PLACEHOLDER')}
                onChange={(e) => setSelectedLanguage(e)}
            />
        </div>
        <div css={floatingModalSelect}>
            <span css={css`display:inline-block; margin-bottom: 5px; ${LargeText}`}>{getResource(props.resources, 'USER_PREFERENCE_MODAL_DATE')}</span>
            <Dropdown<PreferencesEntry>
                resources={props.resources}
                items={dateTimeArray}
                getValue={getMenuValue}
                getLabel={getMenuLabel}
                selectedItem={selectedDateFormat}
                noItemsMessage={""}
                placeholderMessage={getResource(props.resources, 'USER_PREFERENCE_MODAL_DATETIME_PLACEHOLDER')}
                onChange={(e) => setSelectedDateFormat(e)}
            />
        </div>
        <div>
            <button css={mappingButtonSubmit} onClick={() => saveChanges(resourceMap)}>{getResource(props.resources, 'USER_PREFERENCE_MODAL_APPLY')}</button>
        </div>
    </div>
    )
}